const NEWS_CATS = ["League Announcements", "Franchise News", "Player News", "Tournament Updates", "Sponsorship Announcements", "Media Releases"];

const f = (name, label, type = "text", extra = {}) => ({ name, label, type, ...extra });

export const RESOURCES = {
  teams: {
    title: "Teams", single: "team",
    columns: [["number", "#"], ["name", "Name"], ["city", "City"], ["status", "Status"]],
    defaults: { number: 13, name: "", slug: "", short_name: "", city: "", primary_color: "#00E5FF", logo: "", banner: "", status: "coming_soon", description: "", owner_name: "", owner_approved: false, coaches: "" },
    fields: [
      f("number", "Number", "number"), f("name", "Team name"), f("slug", "URL slug", "text", { hint: "lowercase-with-dashes" }),
      f("short_name", "Short name"), f("city", "City / regional identity"), f("primary_color", "Primary colour", "color"),
      f("status", "Announcement status", "select", { options: ["coming_soon", "announced"] }),
      f("logo", "Team logo", "image"), f("banner", "Team banner", "image"),
      f("description", "Team identity / description", "textarea"),
      f("owner_name", "Owner details"), f("owner_approved", "Owner details approved for publishing", "checkbox"),
      f("coaches", "Coaching staff (one per line, e.g. Name — Head Coach)", "textarea"),
    ],
  },
  players: {
    title: "Player profiles", single: "player",
    columns: [["name", "Name"], ["player_role", "Role"], ["team_id", "Team"], ["published", "Published"]],
    defaults: { name: "", team_id: "", player_role: "", batting_hand: "", bowling_style: "", district: "", state: "", photo: "", bio: "", published: false, stats: { matches: 0, runs: 0, wickets: 0, highest: "", best_bowling: "", strike_rate: 0, economy: 0 } },
    fields: [
      f("name", "Name"), f("team_id", "Squad (team)", "team"),
      f("player_role", "Role", "select", { options: ["Batter", "Bowler", "All-rounder", "Wicket-keeper"] }),
      f("batting_hand", "Batting hand", "select", { options: ["Right-hand", "Left-hand"] }),
      f("bowling_style", "Bowling style"), f("district", "District / city"), f("state", "State"),
      f("photo", "Photo", "image"), f("bio", "Bio", "textarea"),
      f("stats.matches", "Matches", "number"), f("stats.runs", "Runs", "number"), f("stats.highest", "Highest score"),
      f("stats.strike_rate", "Strike rate", "number"), f("stats.wickets", "Wickets", "number"), f("stats.best_bowling", "Best bowling"), f("stats.economy", "Economy", "number"),
      f("published", "Publish profile on website (approved players only)", "checkbox"),
    ],
  },
  matches: {
    title: "Matches & scorecards", single: "match", scorecard: true,
    columns: [["number", "Match"], ["date", "Date"], ["team_a_id", "Team A"], ["team_b_id", "Team B"], ["status", "Status"]],
    defaults: { number: 1, date: "", time: "", venue_id: "", team_a_id: "", team_b_id: "", stage: "", status: "scheduled", result: "", toss: "", potm: "", live_feed_id: "", innings: [] },
    fields: [
      f("number", "Match number", "number"), f("stage", "Stage (e.g. League, Final)"),
      f("date", "Date", "date"), f("time", "Time", "time"), f("venue_id", "Venue", "venue"),
      f("team_a_id", "Team A", "team"), f("team_b_id", "Team B", "team"),
      f("status", "Match status", "select", { options: ["scheduled", "live", "completed", "abandoned"] }),
      f("result", "Result"), f("toss", "Toss"), f("potm", "Player of the match"),
      f("live_feed_id", "Live score feed ID (future integration)"),
    ],
  },
  standings: {
    title: "Points table", single: "standing",
    columns: [["team_id", "Team"], ["played", "P"], ["won", "W"], ["lost", "L"], ["points", "Pts"], ["nrr", "NRR"]],
    defaults: { team_id: "", played: 0, won: 0, lost: 0, no_result: 0, points: 0, nrr: 0, form: "" },
    fields: [
      f("team_id", "Team", "team"), f("played", "Matches played", "number"), f("won", "Won", "number"), f("lost", "Lost", "number"),
      f("no_result", "No result", "number"), f("points", "Points", "number"), f("nrr", "Net run rate", "number"),
      f("form", "Recent form (e.g. W,L,W)"),
    ],
  },
  venues: {
    title: "Venues", single: "venue", noCreate: true,
    columns: [["city", "City"], ["state", "State"], ["venue_name", "Venue"], ["confirmed", "Confirmed"]],
    defaults: {},
    fields: [
      f("slug", "Slug"), f("city", "City"), f("state", "State"), f("intro", "City introduction", "textarea"),
      f("venue_name", "Venue name (leave blank until confirmed)"), f("address", "Address"),
      f("facilities", "Facilities", "textarea"), f("directions", "Directions", "textarea"), f("matchday_info", "Matchday information", "textarea"),
      f("image", "Image", "image"), f("confirmed", "Venue officially confirmed", "checkbox"),
    ],
  },
  news: {
    title: "News", single: "article",
    columns: [["title", "Title"], ["category", "Category"], ["published_at", "Date"], ["status", "Status"]],
    defaults: { title: "", slug: "", category: "League Announcements", featured_image: "", published_at: new Date().toISOString().slice(0, 10), author: "", content: "", seo_title: "", meta_description: "", status: "draft", is_demo: false, team_id: "" },
    fields: [
      f("title", "Title"), f("slug", "URL slug", "text", { hint: "lowercase-with-dashes", slugFrom: "title" }),
      f("category", "Category", "select", { options: NEWS_CATS }), f("published_at", "Publication date", "date"), f("author", "Author"),
      f("featured_image", "Featured image", "image"), f("content", "Content (blank line between paragraphs)", "textarea", { rows: 10 }),
      f("seo_title", "SEO title"), f("meta_description", "Meta description", "textarea"),
      f("team_id", "Related team (optional)", "team"),
      f("status", "Status", "select", { options: ["draft", "published"] }), f("is_demo", "Mark as demo content", "checkbox"),
    ],
  },
  gallery: {
    title: "Gallery", single: "gallery item",
    columns: [["title", "Title"], ["scope", "Section"], ["kind", "Type"]],
    defaults: { title: "", image: "", scope: "league", team_id: "", kind: "photo", video_url: "", is_demo: false },
    fields: [
      f("title", "Title / alt text"), f("image", "Image / thumbnail", "image"),
      f("scope", "Show in", "select", { options: ["league", "hyderabad", "amaravati", "media", "team"] }),
      f("team_id", "Team (if section is team)", "team"), f("kind", "Type", "select", { options: ["photo", "video"] }),
      f("video_url", "Video URL (for video items)"), f("is_demo", "Mark as demo content", "checkbox"),
    ],
  },
  sponsors: {
    title: "Approved sponsors", single: "sponsor",
    columns: [["name", "Name"], ["tier", "Tier"], ["status", "Status"]],
    defaults: { name: "", tier: "Official Partner", logo: "", website: "", status: "hidden" },
    fields: [
      f("name", "Sponsor name"), f("tier", "Tier", "select", { options: ["Title Sponsor", "Associate Sponsor", "Official Partner", "Team Sponsor", "Digital Partner"] }),
      f("logo", "Logo", "image"), f("website", "Website"),
      f("status", "Status", "select", { options: ["approved", "hidden"], hint: "Only approved sponsors appear on the website" }),
    ],
  },
};
